<template>
	<div
		v-if="hasDetails"
		class="w-full mt-2"
	>
		<!-- Toggle Button -->
		<button
			type="button"
			class="flex items-center justify-center w-full gap-1 px-3 py-2 text-xs font-medium transition-colors rounded-lg text-mx-gray-500 dark:text-mx-gray-400 hover:bg-mx-gray-50 dark:hover:bg-mx-green-700"
			:aria-expanded="isExpanded"
			:aria-controls="`technical-details-${componentId}`"
			@click="toggleExpanded"
		>
			<!-- Chevron Icon -->
			<svg
				xmlns="http://www.w3.org/2000/svg"
				viewBox="0 0 20 20"
				fill="currentColor"
				class="w-4 h-4 transition-transform"
				:class="{ 'rotate-180': isExpanded }"
			>
				<path
					fill-rule="evenodd"
					d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
					clip-rule="evenodd"
				/>
			</svg>
			<span>{{ isExpanded ? 'Hide' : 'Show' }} Technical Details</span>
		</button>

		<!-- Details Content (collapsible) -->
		<Transition
			name="details-expand"
			@enter="onEnter"
			@after-enter="onAfterEnter"
			@leave="onLeave"
			@after-leave="onAfterLeave"
		>
			<div
				v-show="isExpanded"
				:id="`technical-details-${componentId}`"
				class="overflow-hidden"
			>
				<div class="p-3 mt-2 space-y-1 border rounded-lg shadow-inner bg-mx-gray-50 dark:bg-mx-green-700 border-mx-gray-200 dark:border-mx-green-800 isolate">
					<!-- Key-Value Pairs -->
					<div
						v-for="detail in formattedDetails"
						:key="detail.key"
						class="flex gap-2 text-xs"
					>
						<!-- Key (formatted label) -->
						<span class="flex-shrink-0 font-medium text-mx-gray-500 dark:text-mx-gray-400">
							{{ detail.displayLabel }}:
						</span>
						<!-- Value -->
						<span class="font-mono break-all text-mx-gray-700 dark:text-mx-gray-200">
							{{ detail.value }}
						</span>
					</div>

					<!-- Empty State (shouldn't happen but defensive) -->
					<div
						v-if="formattedDetails.length === 0"
						class="text-xs italic text-center text-mx-gray-400 dark:text-mx-gray-500"
					>
						No technical details available
					</div>
				</div>
			</div>
		</Transition>
	</div>
</template>

<script setup lang="ts">
import { formatFullDetails, hasDisplayableDetails } from '@/utils/equipment/fullDetailsFormatter'

interface Props {
	/** The fullDetails object from device status response */
	details: Record<string, any> | null | undefined
}

const props = defineProps<Props>()

// Generate unique component ID for ARIA
const componentId = ref(`${Math.random().toString(36).substring(2, 9)}`)
const isExpanded = ref(false)
const formattedDetails = computed(() => formatFullDetails(props.details))
const hasDetails = computed(() => hasDisplayableDetails(props.details))

// Toggle expansion
const toggleExpanded = () => {
	isExpanded.value = !isExpanded.value
}

// Transition hooks for smooth height animation
const onEnter = (el: Element) => {
	const element = el as HTMLElement
	element.style.height = '0'
	// Force reflow
	element.offsetHeight
	element.style.height = `${element.scrollHeight}px`
}

const onAfterEnter = (el: Element) => {
	const element = el as HTMLElement
	element.style.height = 'auto'
}

const onLeave = (el: Element) => {
	const element = el as HTMLElement
	element.style.height = `${element.scrollHeight}px`
	// Force reflow
	element.offsetHeight
	element.style.height = '0'
}

const onAfterLeave = (el: Element) => {
	const element = el as HTMLElement
	element.style.height = 'auto'
}
</script>

<style scoped>
.details-expand-enter-active,
.details-expand-leave-active {
	transition: height 0.3s ease-in-out;
}

.details-expand-enter-from,
.details-expand-leave-to {
	height: 0;
}
</style>
